import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card, Button, FilterTypes, showSnackbar } from "@cofano/cck";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Language from "components/utils/Language";
import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { DataFormat, usePermission } from "imports/Utils";

import { ChildPropsFromPagination } from "types/commons";
import { GetConnectingVesselsDocument, GetConnectingVesselsQuery } from "__generated__/view/connectingVessels/graphql/connectingVessel.queries.generated";
import { DeleteConnectingVesselDocument, DeleteConnectingVesselMutation } from "__generated__/view/connectingVessels/graphql/connectingVessel.mutations.generated";

type ConnectingVesselFromQuery = ChildPropsFromPagination<GetConnectingVesselsQuery, "connectingVessels">

const ConnectingVessels = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const client = useApolloClient();

	const [ deleteConnectingVessel ] = useMutation<DeleteConnectingVesselMutation>(DeleteConnectingVesselDocument);

	const { isReadOnly } = usePermission();

	const { tableProps, filterProps, filterValuesProps, refetchAfterDelete } = useBackendTable({
		id: TableId.ConnectingVessels,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const response = await client.query({
					query: GetConnectingVesselsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						search: {
							name: filter.name,
							mmsi: filter.mmsi ? Math.round(filter.mmsi) : undefined,
							imo: filter.imo ? Math.round(filter.imo) : undefined,
							flagCode: filter.flagCode,
							flagName: filter.flagName,
							callSign: filter.callSign,
							tracking: filter.tracking,
						},
					},
				});

				callback({
					content: response.data?.connectingVessels?.content ?? [],
					totalResults: response.data?.connectingVessels.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const onDeleteConnectingVessel = (id: number) => {
		deleteConnectingVessel({
			variables: {
				id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			refetchAfterDelete();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("CONNECTING_VESSELS")}
					<Helmet title={intl.formatMessage({ id: "CONNECTING_VESSELS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/connectingvessels/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box sx={{ mb: 2 }}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "name",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_NAME" }),
							}, {
								id: "mmsi",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_MMSI" }),
							}, {
								id: "imo",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_IMO" }),
							}, {
								id: "callSign",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_CALLSIGN" }),
							}, {
								id: "flagName",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGNAME" }),
							}, {
								id: "flagCode",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGCODE" }),
							}, {
								id: "tracking",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_TRACKING" }),
								renderFilterValue: (value) => DataFormat.boolean(value),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "name",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_NAME" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "mmsi",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_MMSI" }),
								filterType: FilterTypes.NUMBER,
							}, {
								id: "imo",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_IMO" }),
								filterType: FilterTypes.NUMBER,
							}, {
								id: "callSign",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_CALLSIGN" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "flagName",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGNAME" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "flagCode",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGCODE" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "tracking",
								title: intl.formatMessage({ id: "CONNECTING_VESSEL_TRACKING" }),
								filterType: FilterTypes.BOOLEAN,
							} ]} />
					)}
					columns={[ {
						id: "name",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_NAME" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "mmsi",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_MMSI" }),
						sortable: true,
						filterComponent: FilterTypes.NUMBER,
					}, {
						id: "imo",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_IMO" }),
						sortable: true,
						filterComponent: FilterTypes.NUMBER,
					}, {
						id: "callSign",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_CALLSIGN" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "flagName",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGNAME" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "flagCode",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_FLAGCODE" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "tracking",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL_TRACKING" }),
						sortable: true,
						filterComponent: FilterTypes.BOOLEAN,
						render: (row: ConnectingVesselFromQuery) => DataFormat.boolean(row.tracking),
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/connectingvessels/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteConnectingVessel(row.id);
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default ConnectingVessels;
